import { TableCard } from "../shared";
import { jiraTicketsColumnDefs as columnDefs } from "../col_defs";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axios";
import { useStorageManager } from "../../../shared/hooks";
import eventEmitter from "../../../components/utils/eventEmitter";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";
import { has } from "lodash";
import { ROLES_CONTENT_PROPS } from "../../../shared/constants/constant-data";
import { Box, CircularProgress } from "@mui/material";

const PAGE_SIZE = 10;
const SCROLL_THROTTLE_MS = 500;

interface JiraTicket {
  issue_key: string;
  ticket_url: string | null;
  summary: string | null;
  status: string | null;
  priority: string;
}

const JiraTickets = () => {
  const { getFromLocalStorage } = useStorageManager();
  const [tableData, setTableData] = useState<JiraTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  const { hasPermissions } = useRolePermissions();

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });

  const gridRef = useRef<any>(null);
  const isFetchingNext = useRef(false);
  const isFetchingPrev = useRef(false);
  const lastScrollTs = useRef<number>(0);
  const firstVisibleRowRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    const handleJiraDelete = () => {
      console.log("🧹 Jira deleted → clearing tickets");

      setTableData([]);
      setPagination({
        page: 1,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      });
      setLoading(false);
    };

    eventEmitter.on("jiraIntegrationDeleted", handleJiraDelete);

    return () => {
      eventEmitter.off("jiraIntegrationDeleted", handleJiraDelete);
    };
  }, []);

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger(prev => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on('filterSaved', handleFilterSaved);

    return () => {
      eventEmitter.off('filterSaved', handleFilterSaved);
    };
  }, []);

  // Initial fetch - FIXED: removed fetchData from dependencies
  useEffect(() => {
    if (hasPermissions(ROLES_CONTENT_PROPS.JIRA_INTEGRATION) || hasPermissions(ROLES_CONTENT_PROPS.JIRA_VIEW_ONLY) || hasPermissions(ROLES_CONTENT_PROPS.JIRA_CREATE_MODIFY)) {
      setTableData([]);
      setPagination({
        page: 1,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      });
      fetchData({ page: 1, mode: "initial" });
    } else {
      setTableData([]);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  // Fixed fetchData function
  const fetchData = useCallback(
    async ({
      page = 1,
      mode = "initial",
    }: {
      page?: number;
      mode?: "initial" | "append" | "prepend";
    } = {}) => {
      // Capture scroll state before fetching
      try {
        const apiBefore = gridRef.current?.api;
        if (apiBefore && mode !== "initial") {
          firstVisibleRowRef.current = typeof apiBefore.getFirstDisplayedRow === "function" ? apiBefore.getFirstDisplayedRow() : firstVisibleRowRef.current;
          const vRangeBefore = apiBefore.getVerticalPixelRange?.();
          scrollPositionRef.current = vRangeBefore?.top ?? scrollPositionRef.current;
        }
      } catch (err) {
        console.warn("Could not capture pre-fetch scroll state for JiraTickets", err);
      }

      if (mode === "initial") {
        setLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const userDetails = JSON.parse(getFromLocalStorage("userDetails") || "{}");
        const organizationId = userDetails.organization_id;

        if (!organizationId) {
          throw new Error("Organization ID not found");
        }

        const response = await axiosInstance.get(
          `jira-events/jira-tickets`, {
          params: {
            org_id: organizationId,
            page,
            page_size: PAGE_SIZE,
          }
        }
        );

        // Debug: log API response
        console.log("📥 Jira Tickets API Response:", {
          page,
          dataType: Array.isArray(response.data) ? "array" : typeof response.data,
          dataLength: Array.isArray(response.data) ? response.data.length : "N/A",
          pagination: response.data?.pagination,
        });

        let ticketsData = [];
        let pageInfo = null;

        // Check if response has pagination structure
        if (response.data?.data && response.data?.pagination) {
          ticketsData = response.data.data;
          pageInfo = response.data.pagination;
        } else if (Array.isArray(response.data)) {
          ticketsData = response.data;
          // If no pagination info, assume no more pages
          pageInfo = {
            page: page,
            total_pages: page,
            has_next: false,
            has_previous: page > 1,
          };
        }

        setPagination({
          page: pageInfo?.page || page,
          totalPages: pageInfo?.total_pages || 1,
          hasNext: pageInfo?.has_next || false,
          hasPrevious: pageInfo?.has_previous || false,
        });

        setTableData((prev) => {
          if (mode === "initial") {
            console.log("✅ Setting initial data:", ticketsData?.length ?? 0, "items");
            // Transform the data to ensure all required fields are present
            const transformedData = ticketsData.map((ticket: any) => ({
              issue_key: ticket.issue_key || "",
              ticket_url: ticket.ticket_url || "",
              summary: ticket.summary || "",
              status: ticket.status || "",
              priority: ticket.priority || "Highest"
            }));
            return transformedData;
          }

          const itemsToAdd = ticketsData.map((ticket: any) => ({
            issue_key: ticket.issue_key || "",
            ticket_url: ticket.ticket_url || "",
            summary: ticket.summary || "",
            status: ticket.status || "",
            priority: ticket.priority || "Highest"
          }));

          if (mode === "append") {
            console.log(
              "➕ Appending:",
              itemsToAdd.length,
              "items to",
              prev.length,
              "existing"
            );
            return [...prev, ...itemsToAdd];
          }
          if (mode === "prepend") {
            console.log(
              "⬆️ Prepending:",
              itemsToAdd.length,
              "items to",
              prev.length,
              "existing"
            );
            return [...itemsToAdd, ...prev];
          }

          return prev;
        });
        // Restore scroll position after DOM updates
        try {
          const apiAfter = gridRef.current?.api;
          if (apiAfter) {
            if (mode === "append") {
              const indexToShow = firstVisibleRowRef.current ?? 0;
              setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
            } else if (mode === "prepend") {
              const indexToShow = (firstVisibleRowRef.current ?? 0) + (ticketsData?.length || 0);
              setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
            }
          }
        } catch (err) {
          console.warn("Could not restore scroll for JiraTickets", err);
        }
      } catch (error) {
        console.error("Error fetching:", error);
        // toast.error("Failed to fetch Jira tickets");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
        isFetchingNext.current = false;
        isFetchingPrev.current = false;
      }
    },
    [getFromLocalStorage]
  );

  // Throttled infinite scroll handler - FIXED: use ref for pagination to avoid recreating
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const handleScrollEnd = useCallback(() => {
    const now = Date.now();
    if (now - lastScrollTs.current < SCROLL_THROTTLE_MS) {
      return;
    }
    lastScrollTs.current = now;

    const gridApi = gridRef.current?.api;
    if (!gridApi) {
      return;
    }

    try {
      // Get the total row count from the data
      const totalRowCount = tableData.length;
      if (totalRowCount === 0) return;

      // Get viewport info from the event or API
      const verticalPixelRange = gridApi.getVerticalPixelRange?.();

      if (!verticalPixelRange) {
        // Fallback: use simple row count check
        const currentPagination = paginationRef.current;

        // If we have all data loaded and user scrolled, check if we need more
        if (currentPagination.hasNext && !isFetchingNext.current) {
          console.log("⬇️ Loading next page:", currentPagination.page + 1);
          isFetchingNext.current = true;
          fetchData({ page: currentPagination.page + 1, mode: "append" });
        }
        return;
      }

      // Calculate scroll position
      const { top, bottom } = verticalPixelRange;
      const scrollTop = top;
      const scrollHeight =
        gridApi.getModel?.()?.getRowCount?.() * 50 || totalRowCount * 50; // Assume 50px row height
      const clientHeight = bottom - top;

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      const isNearBottom = scrollPercentage > 0.8; // 80% scrolled
      const isNearTop = scrollTop < 100; // Less than 100px from top

      const currentPagination = paginationRef.current;

      console.log("📜 Scroll:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollPercentage: (scrollPercentage * 100).toFixed(1) + "%",
        isNearBottom,
        isNearTop,
        hasNext: currentPagination.hasNext,
        hasPrev: currentPagination.hasPrevious,
      });

      // Load next page when near bottom
      if (
        isNearBottom &&
        currentPagination.hasNext &&
        !isFetchingNext.current
      ) {
        console.log("⬇️ Loading next page:", currentPagination.page + 1);
        isFetchingNext.current = true;
        fetchData({ page: currentPagination.page + 1, mode: "append" });
        return;
      }

      // Load previous page when near top
      if (
        isNearTop &&
        currentPagination.hasPrevious &&
        !isFetchingPrev.current
      ) {
        console.log("⬆️ Loading previous page:", currentPagination.page - 1);
        isFetchingPrev.current = true;
        fetchData({ page: currentPagination.page - 1, mode: "prepend" });
        return;
      }
    } catch (err) {
      console.error("Scroll handler error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData.length]);

  const onRowClick = (data: JiraTicket) => {
    if (data.ticket_url) {
      window.open(data.ticket_url, '_blank');
    }
  };

  const memoizedGridOptions = useMemo(
    () => ({
      rowHeight: 50,
      domLayout: "normal",
      suppressScrollOnNewData: true,
      onBodyScrollEnd: handleScrollEnd,
    }),
    [handleScrollEnd]
  );

  return (
    <>
      <TableCard
        title="Recently Added Tickets"
        loading={loading}
        columnDefs={columnDefs}
        rowData={tableData}
        showSearch={false}
        isRowSelectable={false}
        onRowClick={onRowClick}
        gridRef={gridRef}
        gridOptions={memoizedGridOptions}
      />
      {/* Loading indicator for pagination
      {isFetchingMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 1,
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )} */}
    </>
  );
};

export default JiraTickets; 